/**
 * VariableDestinationPicker
 * ---------------------------------------------------------------------------
 * Seletor compacto para campos que ESCREVEM em variáveis do fluxo.
 * Abre o explorador no modo de destino, portanto somente variáveis
 * personalizadas e graváveis podem ser escolhidas.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-07 v1.0.0 - Criação do seletor reutilizável de destino.
 */

import { useState } from 'react';
import { ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenantVariables, type TenantVariable } from '@/hooks/useTenantVariables';
import { VariableExplorerModal } from './variables/VariableExplorerModal';

interface VariableDestinationPickerProps {
  tenantId: string;
  /** ID ou nome legado da variável já persistida no node. */
  value?: string;
  /** Nome persistido em separado, usado enquanto a lista ainda carrega. */
  valueName?: string;
  onChange: (variable: TenantVariable | null) => void;
  allowClear?: boolean;
  placeholder?: string;
}

/**
 * Mantém o campo compacto no formulário e delega seleção/criação ao modal novo.
 */
export function VariableDestinationPicker({
  tenantId,
  value,
  valueName,
  onChange,
  allowClear = true,
  placeholder = 'Selecione ou crie uma variável',
}: VariableDestinationPickerProps) {
  const [open, setOpen] = useState(false);
  const { variables, isLoading } = useTenantVariables(tenantId);

  const selectedVariable = variables.find(
    (variable) => variable.id === value || variable.name === value,
  );
  const selectedLabel = selectedVariable?.display_name
    || selectedVariable?.name
    || valueName
    || (value && !value.includes('-') ? value : '');

  return (
    <>
      <div className="relative w-full">
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={!tenantId || isLoading}
          onClick={() => setOpen(true)}
        >
          <span className={selectedLabel ? 'truncate' : 'truncate text-muted-foreground'}>
            {selectedLabel || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>

        {allowClear && value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-8 top-1/2 z-10 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-destructive"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onChange(null);
            }}
            aria-label="Limpar seleção"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <VariableExplorerModal
        open={open}
        onOpenChange={setOpen}
        tenantId={tenantId}
        mode="destination"
        onInsert={() => undefined}
        onSelect={onChange}
      />
    </>
  );
}